import { createFileRoute, useRouter } from '@tanstack/react-router'
import * as React from 'react'
import { Frame } from 'regen-ui'
import * as z from 'zod/mini'

import { Centered } from '#/routes/-layouts/Centered.js'
import * as PostMessage from '#/routes/remote/-lib/PostMessage.js'

const searchSchema = z.looseObject({
  accent: z.optional(z.string()),
  mode: z.optional(z.union([z.literal('iframe'), z.literal('popup')])),
  origin: z.optional(z.string()),
  radius: z.optional(z.string()),
  scheme: z.optional(z.string()),
})

/** PostMessage endpoint for WATA browser sessions (the SDK's `postMessage` adapter). */
export const Route = createFileRoute('/post-message')({
  component: RouteComponent,
  validateSearch: searchSchema,
})

function RouteComponent() {
  const router = useRouter()
  const search = Route.useSearch()
  const [error, setError] = React.useState<string>()

  React.useEffect(() => {
    if (!window.opener && window.parent === window) {
      setError('This page answers wallet requests from an app. Open it from one.')
      return
    }
    try {
      PostMessage.listen(router, {
        ...(search.mode ? { mode: search.mode } : {}),
        origin: PostMessage.parseOrigin(search.origin),
        theme: { accent: search.accent, radius: search.radius, scheme: search.scheme },
      })
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error))
    }
  }, [router, search.accent, search.mode, search.origin, search.radius, search.scheme])

  React.useEffect(() => {
    const origin = (() => {
      try {
        return PostMessage.parseOrigin(search.origin)
      } catch {
        return undefined
      }
    })()
    if (!origin) return

    const listener = (event: MessageEvent) => {
      if (event.origin !== origin) return
      if (!isMoonPayRelay(event.data)) return
      if (!isMoonPayOrigin(event.data.origin)) return
      for (const frame of document.querySelectorAll('iframe')) {
        if (!frame.contentWindow) continue
        const src = (() => {
          try {
            return new URL(frame.src)
          } catch {
            return undefined
          }
        })()
        if (src?.origin !== window.location.origin) continue
        frame.contentWindow.postMessage(event.data, window.location.origin)
      }
    }

    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [search.origin])

  if (error)
    return (
      <Centered>
        <Frame subtitle={error} title="Invalid Request">
          <p className="text-copy-14 text-negative">
            The app sent a request the wallet could not read.
          </p>
        </Frame>
      </Centered>
    )

  // Nothing to render: in iframe mode the page is eager-loaded and hidden, so
  // it stays transparent until a request mounts an approval card — at which
  // point the dim and the card animate in together (see the approval Backdrop).
  // The request lands within a frame or two of the consumer's handshake.
  return null
}

function isMoonPayRelay(value: unknown): value is {
  __tempoMoonPayRelay: true
  data: unknown
  origin: string
} {
  if (!value || typeof value !== 'object') return false
  const relay = value as Record<string, unknown>
  return relay.__tempoMoonPayRelay === true && typeof relay.origin === 'string'
}

function isMoonPayOrigin(origin: string) {
  return (
    /^https:\/\/([a-zA-Z0-9-]+\.)*moonpay\.com$/.test(origin) ||
    /^https:\/\/([a-zA-Z0-9-]+\.)*moonpaycloud\.com$/.test(origin) ||
    /^https:\/\/([a-zA-Z0-9-]+\.)*preview\.moonpay-dev\.com$/.test(origin)
  )
}
